import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  SavedLocation,
  CreateSavedLocationInput,
  UpdateSavedLocationInput,
} from "../lib/api";

interface SavedLocationFormProps {
  location?: SavedLocation;
  onClose: () => void;
  onSuccess: () => void;
}

function SavedLocationForm({
  location,
  onClose,
  onSuccess,
}: SavedLocationFormProps) {
  const isEdit = !!location;
  const [name, setName] = useState(location?.name ?? "");
  const [street, setStreet] = useState(location?.address?.street ?? "");
  const [city, setCity] = useState(location?.address?.city ?? "");
  const [state, setState] = useState(location?.address?.state ?? "");
  const [zipCode, setZipCode] = useState(location?.address?.zipCode ?? "");
  const [notes, setNotes] = useState(location?.notes ?? "");
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: CreateSavedLocationInput) =>
      api.savedLocations.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create location");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateSavedLocationInput) =>
      api.savedLocations.update(location!.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to update location");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Location name is required");
      return;
    }

    const hasAddress =
      street.trim() || city.trim() || state.trim() || zipCode.trim();

    const data: CreateSavedLocationInput = {
      name: name.trim(),
      address: hasAddress
        ? {
            street: street.trim() || undefined,
            city: city.trim() || undefined,
            state: state.trim() || undefined,
            zipCode: zipCode.trim() || undefined,
          }
        : null,
      notes: notes.trim() || null,
    };

    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {isEdit ? "Edit Location" : "Add New Location"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="locationName"
              className="block text-sm font-medium text-gray-700"
            >
              Location name
            </label>
            <input
              id="locationName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div>
            <label
              htmlFor="street"
              className="block text-sm font-medium text-gray-700"
            >
              Street address
            </label>
            <input
              id="street"
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label
                htmlFor="city"
                className="block text-sm font-medium text-gray-700"
              >
                City
              </label>
              <input
                id="city"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
            </div>
            <div>
              <label
                htmlFor="state"
                className="block text-sm font-medium text-gray-700"
              >
                State
              </label>
              <input
                id="state"
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
            </div>
            <div>
              <label
                htmlFor="zipCode"
                className="block text-sm font-medium text-gray-700"
              >
                Zip code
              </label>
              <input
                id="zipCode"
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="notes"
              className="block text-sm font-medium text-gray-700"
            >
              Notes
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              placeholder="Parking instructions, access codes, etc."
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending
                ? "Saving..."
                : isEdit
                  ? "Save Changes"
                  : "Create Location"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  location: SavedLocation;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  error?: string;
}

function DeleteConfirmModal({
  location,
  onClose,
  onConfirm,
  isPending,
  error,
}: DeleteConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Delete Location?</h2>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete &quot;{location.name}&quot;? This
          action cannot be undone.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatAddress(location: SavedLocation): string {
  const parts = [];
  if (location.address?.street) parts.push(location.address.street);
  if (location.address?.city) parts.push(location.address.city);
  if (location.address?.state) parts.push(location.address.state);
  if (location.address?.zipCode) parts.push(location.address.zipCode);
  return parts.join(", ") || "-";
}

function SavedLocationRow({
  location,
  onEdit,
  onDelete,
}: {
  location: SavedLocation;
  onEdit: (location: SavedLocation) => void;
  onDelete: (location: SavedLocation) => void;
}) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="font-medium text-gray-900">{location.name}</span>
      </td>
      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
        {formatAddress(location)}
      </td>
      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
        {location.notes || "-"}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
        <button
          onClick={() => onEdit(location)}
          className="text-amber-600 hover:text-amber-700 mr-4"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(location)}
          className="text-red-600 hover:text-red-700"
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

export function SavedLocationsPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<SavedLocation | null>(
    null
  );
  const [deletingLocation, setDeletingLocation] =
    useState<SavedLocation | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["savedLocations"],
    queryFn: () => api.savedLocations.list(),
  });

  const [deleteError, setDeleteError] = useState("");

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.savedLocations.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["savedLocations"] });
      setDeletingLocation(null);
      setDeleteError("");
    },
    onError: (err) => {
      setDeleteError(
        err instanceof ApiError ? err.message : "Failed to delete location"
      );
    },
  });

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["savedLocations"] });
  };

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            {error instanceof ApiError
              ? error.message
              : "Failed to load locations"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saved Locations</h1>
          <p className="text-sm text-gray-500 mt-1">
            Meeting spots for mobile services.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
        >
          Add Location
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-pulse text-gray-500">Loading locations...</div>
        </div>
      ) : data?.data.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">
            No saved locations yet. Add your first location to get started.
          </p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.data.map((location) => (
                <SavedLocationRow
                  key={location.id}
                  location={location}
                  onEdit={setEditingLocation}
                  onDelete={setDeletingLocation}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <SavedLocationForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleSuccess}
        />
      )}

      {editingLocation && (
        <SavedLocationForm
          location={editingLocation}
          onClose={() => setEditingLocation(null)}
          onSuccess={handleSuccess}
        />
      )}

      {deletingLocation && (
        <DeleteConfirmModal
          location={deletingLocation}
          onClose={() => {
            setDeletingLocation(null);
            setDeleteError("");
          }}
          onConfirm={() => deleteMutation.mutate(deletingLocation.id)}
          isPending={deleteMutation.isPending}
          error={deleteError}
        />
      )}
    </div>
  );
}
